import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UsePWAInstallReturn {
  canInstall: boolean;
  isIOS: boolean;
  isInstalled: boolean;
  isAndroid: boolean;
  promptInstall: () => Promise<boolean>;
  dismissPrompt: (cooldownDays?: number) => void;
  shouldShowBanner: boolean;
}

const STORAGE_KEY_DISMISSED = 'pwa-install-dismissed';
const STORAGE_KEY_INSTALLED = 'pwa-installed';
const DEFAULT_COOLDOWN_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

interface DismissalRecord {
  at: number;
  days: number;
}

function readDismissal(): DismissalRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DISMISSED);
    if (!raw) return null;
    // New JSON shape
    if (raw.startsWith('{')) {
      const parsed = JSON.parse(raw);
      if (typeof parsed?.at === 'number' && typeof parsed?.days === 'number') {
        return parsed;
      }
      return null;
    }
    // Legacy: bare timestamp string
    const at = Number(raw);
    if (!Number.isFinite(at)) return null;
    return { at, days: DEFAULT_COOLDOWN_DAYS };
  } catch {
    return null;
  }
}

function isCooldownActive(): boolean {
  const rec = readDismissal();
  if (!rec) return false;
  return Date.now() - rec.at < rec.days * DAY_MS;
}

export function usePWAInstall(): UsePWAInstallReturn {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [shouldShowBanner, setShouldShowBanner] = useState(false);
  const [cooldownActive, setCooldownActive] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return isCooldownActive();
  });

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  const isAndroid = /Android/.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    const hasInstalledFlag = localStorage.getItem(STORAGE_KEY_INSTALLED) === 'true';
    const installed = isStandalone || hasInstalledFlag;

    setIsInstalled(installed);
    if (isStandalone) localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handler = (e: MediaQueryListEvent) => {
      setIsInstalled(e.matches);
      if (e.matches) localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
    };
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);

      // If browser says we can install, app is not currently installed
      localStorage.removeItem(STORAGE_KEY_INSTALLED);
      setIsInstalled(false);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  useEffect(() => {
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      localStorage.setItem(STORAGE_KEY_INSTALLED, 'true');
      localStorage.removeItem(STORAGE_KEY_DISMISSED);
      setCooldownActive(false);
    };
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => window.removeEventListener('appinstalled', handleAppInstalled);
  }, []);

  // Cross-tab sync: if another tab dismisses (or clears) the prompt, react here.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key && e.key !== STORAGE_KEY_DISMISSED) return;
      setCooldownActive(isCooldownActive());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (isInstalled || !isMobile || cooldownActive) {
      setShouldShowBanner(false);
      return;
    }

    const timer = setTimeout(() => setShouldShowBanner(true), 3000);
    return () => clearTimeout(timer);
  }, [isInstalled, isMobile, cooldownActive]);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) return false;
    try {
      await deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') { setDeferredPrompt(null); return true; }
      return false;
    } catch { return false; }
  }, [deferredPrompt]);

  const dismissPrompt = useCallback((cooldownDays: number = DEFAULT_COOLDOWN_DAYS) => {
    const record: DismissalRecord = { at: Date.now(), days: cooldownDays };
    try {
      localStorage.setItem(STORAGE_KEY_DISMISSED, JSON.stringify(record));
    } catch {
      // ignore quota / privacy mode errors
    }
    setCooldownActive(true);
    setShouldShowBanner(false);
  }, []);

  return { canInstall: Boolean(deferredPrompt) || isIOS, isIOS, isAndroid, isInstalled, promptInstall, dismissPrompt, shouldShowBanner };
}
